import {
    FrequencyUnits,
    IActivityEventResponse,
    IAlertResponse,
    IIndicatorResponse,
    IIssueHealthRangeResponse,
    IIssueMachineHealthResponse,
    ISessionCreateRequest,
    ISessionResponse,
    ITreeItemResponse,
    MetricTypeCodes,
    SensorRoleTypeCodes,
} from '@kcftech/prometheustypescriptapiclient';
import { Subtree } from 'pages/workbench/side_panel/workbench_tree/useLoadSubtree';
import { TimeMarkerId } from 'pages/workbench/workbench_tabs/trend/types';
import { ChartRange } from 'shared/chart/core/types';
import { TimeRange, typeArrayOfAll } from 'utils/types';

import {
    ActivityFilters,
    BurstDataType,
    FrequencyScale,
    IndicatorFilterTabType,
    IndicatorInfo,
    IndicatorSelectionMode,
    MarkerMode,
    SelectedPoint,
    SelectedSeriesPoint,
    SidebandCount,
    TimeMarkerMeta,
    UnifiedChartMetadata,
    WaterfallSelectedPoint,
} from '../types';

export type WorkbenchStateValues = Readonly<{
    isInitialized: boolean;
    runningSpeedHz: number;
    runningSpeedUnit: FrequencyUnits;
    runningSpeedLocked: boolean;
    frequencyBurstDataType: BurstDataType;
    timeBurstDataType: BurstDataType;
    waterfallBurstDataType: BurstDataType;
    sidebandFrequencyHz: number;
    sidebandCount: SidebandCount;
    bearingId: string | undefined;
    rulerMarkerFrequencyHz: number;
    selectedMarkerIds: string[];
    sidebandMarkerId: string | undefined;
    harmonicMarkerIds: string[];
    timeMarkerMeta: TimeMarkerMeta[];
    showActivityLine: boolean;
    isTimeSeriesChartExpanded: boolean;
    showFrequencySpectrumChart: boolean;
    isFrequencySpectrumChartExpanded: boolean;
    showTimeDomainChart: boolean;
    isTimeDomainChartExpanded: boolean;
    showFrequencyMarkerTable: boolean;
    showTimeMarkerTable: boolean;
    showWaterfallValuesTable: boolean;
    isWaterfallChartExpanded: boolean;
    selectedPoint: SelectedSeriesPoint | undefined;
    frequencyDomainSelectedPoint: SelectedPoint | undefined;
    timeDomainSelectedPoint: SelectedPoint | undefined;
    waterfallSelectedPoint: WaterfallSelectedPoint | undefined;
    waterfallFrequencyPoints: SelectedSeriesPoint[];
    showMinMax: boolean;
    showThresholds: boolean;
    autoScroll: boolean;
    splineSmoothing: boolean;
    waterfallSplineSmoothing: boolean;
    showBaseline: boolean;
    timeRange: TimeRange | undefined;
    range: ChartRange | undefined;
    zoomRange: ChartRange | undefined;
    fixedTimeSeriesLeftYAxisRange: ChartRange | undefined;
    timeSeriesLeftYAxisZoom: ChartRange | undefined;
    fixedTimeSeriesRightYAxisRange: ChartRange | undefined;
    timeSeriesRightYAxisZoom: ChartRange | undefined;
    frequencyChartZoomRange: ChartRange | undefined;
    fixedFrequencyChartYAxisRange: ChartRange | undefined;
    frequencyChartYAxisZoom: ChartRange | undefined;
    frequencyChartScale: FrequencyScale;
    timeChartZoomRange: ChartRange | undefined;
    fixedTimeChartYAxisRange: ChartRange | undefined;
    timeChartYAxisZoom: ChartRange | undefined;
    waterfallChartScale: FrequencyScale;
    waterfallChartZoomRange: ChartRange | undefined;
    fixedWaterfallChartYAxisRange: ChartRange | undefined;
    waterfallChartYAxisZoom: ChartRange | undefined;
    waterfallChartSeriesOffset: number | undefined;
    activityFilters: ActivityFilters;
    customIndicatorFilterIds: string[];
    /**
     * An array of indicator types used to filter indicators
     */
    indicatorTypes: MetricTypeCodes[];
    /**
     * An array of sensor role types used to filter indicators
     */
    sensorRoleTypes: SensorRoleTypeCodes[];
    indicatorFilterTabType: IndicatorFilterTabType;
    selectedGroupIds: string[];
    selectedIndicators: IndicatorInfo[];
    indicatorSelectionMode: IndicatorSelectionMode;
    isTreeVisible: boolean;
    expandedGroupIds: string[];
    showTreeStatusIcons: boolean;
    showTreeOfflineIcons: boolean;
    isLoadingIndicators: boolean;

    // health range tagging state
    isAddingHealthRange: boolean;
    selectedHealthRangeIds: string[];
    editingHealthRangeId?: string;
    markerMode: MarkerMode;
}>;

export type WorkbenchStateMethods = Readonly<{
    toggleTreeVisibility: () => void;
    toggleTreeStatusIcons: () => void;
    toggleTreeOfflineIcons: () => void;
    toggleGroupExpanded: (treeItem: Subtree) => void;
    addGroupToExpanded: (groupId: string) => void;
    removeGroupFromExpanded: (groupId: string) => void;
    toggleRunningSpeedLocked: () => void;
    setRunningSpeedHz: (runningSpeedHz: number) => void;
    setRunningSpeedUnit: (unit: FrequencyUnits) => void;
    setFrequencyBurstDataType: (frequencyBurstDataType: BurstDataType) => void;
    setTimeBurstDataType: (timeBurstDataType: BurstDataType) => void;
    setWaterfallBurstDataType: (burstDataType: BurstDataType) => void;
    setSidebandFrequencyHz: (sidebandFrequencyHz: number) => void;
    setSidebandCount: (sidebandCount: SidebandCount) => void;
    setBearing: (bearingId: string) => void;
    clearBearing: () => void;
    setRulerMarkerFrequencyHz: (frequencyHz: number) => void;
    toggleMarkerVisibility: (markerId: string) => void;
    toggleMarkerSidebandVisibility: (markerId: string) => void;
    toggleMarkerHarmonicVisibility: (markerId: string) => void;
    setTimeMarkerZeroCycleMs: (markerId: TimeMarkerId, zeroCycleMs: number) => void;
    toggleTimeMarkerVisibility: (markerId: TimeMarkerId) => void;
    hideAllTimeMarkers: () => void;
    toggleActivityLineVisibility: () => void;
    toggleTimeSeriesChartExpansion: () => void;
    toggleFrequencySpectrumChartVisibility: () => void;
    toggleFrequencySpectrumChartExpansion: () => void;
    toggleTimeDomainChartVisibility: () => void;
    toggleTimeDomainChartExpansion: () => void;
    toggleFrequencyMarkerTableVisibility: () => void;
    toggleTimeMarkerTableVisibility: () => void;
    toggleAllTrendComponents: (canViewWaterfallChart: boolean) => void;
    toggleWaterfallChartExpansion: () => void;
    toggleWaterfallValuesTableVisibility: () => void;
    setSelectedPoint: (
        selectedPoint?: SelectedSeriesPoint,
        resetWaterfall?: boolean
    ) => void;
    setFrequencySelectedPoint: (selectedPoint?: SelectedPoint) => void;
    setTimeDomainSelectedPoint: (selectedPoint?: SelectedPoint) => void;
    setWaterfallSelectedPoint: (waterfallSelectedPoint?: WaterfallSelectedPoint) => void;
    toggleWaterfallPoint: (point: SelectedSeriesPoint) => void;
    setWaterfallPoints: (points: SelectedSeriesPoint[]) => void;
    toggleMinMaxVisibility: () => void;
    toggleThresholdsVisibility: () => void;
    toggleAutoScroll: () => void;
    toggleSplineSmoothing: () => void;
    toggleWaterfallSplineSmoothing: () => void;
    toggleBaselineVisibility: () => void;
    setShowBaseline: (showBaseline: boolean) => void;
    hideAllMarkers: () => void;
    updateAutoScroll: () => void;
    getSessionData: (systemId: string) => ISessionCreateRequest;
    setActivityFilters: (activityFilters: ActivityFilters) => void;
    setTimeRange: (timeRange: TimeRange) => void;
    setZoomRange: (range: ChartRange) => void;
    setFixedTimeSeriesLeftYAxisRange: (range: ChartRange | 'auto') => void;
    setTimeSeriesLeftYAxisZoom: (zoom: ChartRange | 'auto') => void;
    setFixedTimeSeriesRightYAxisRange: (range: ChartRange | 'auto') => void;
    setTimeSeriesRightYAxisZoom: (zoom: ChartRange | 'auto') => void;
    setFixedFrequencyChartYAxisRange: (range: ChartRange | 'auto') => void;
    setFrequencyChartYAxisZoom: (zoom: ChartRange | 'auto') => void;
    setFrequencyChartZoomRange: (zoom: ChartRange) => void;
    setFrequencyChartScale: (frequencyChartScale: FrequencyScale) => void;
    setFixedWaterfallChartYAxisRange: (range: ChartRange | 'auto') => void;
    setWaterfallChartYAxisZoom: (zoom: ChartRange | 'auto') => void;
    setWaterfallChartZoomRange: (zoom: ChartRange) => void;
    setWaterfallChartScale: (frequencyChartScale: FrequencyScale) => void;
    setWaterfallChartSeriesOffset: (offset: number) => void;
    setFixedTimeChartYAxisRange: (range: ChartRange | 'auto') => void;
    setTimeChartYAxisZoom: (zoom: ChartRange | 'auto') => void;
    setTimeChartZoomRange: (zoom: ChartRange) => void;
    resetTimeSeriesZoom: () => void;
    resetFrequencyChartZoom: () => void;
    resetTimeChartZoom: () => void;
    resetWaterfallChartZoom: () => void;
    setIndicatorFilterTabType: (type: IndicatorFilterTabType) => void;
    clearIndicatorFilters: () => void;
    setCustomIndicatorFilterIds: (customIndicatorFilterIds: string[]) => void;
    setIndicatorTypes: (indicatorTypes: MetricTypeCodes[]) => void;
    setSensorRoleTypes: (sensorRoleTypes: SensorRoleTypeCodes[]) => void;
    setSelectedGroup: (groupId: string) => void;
    toggleGroupSelected: (treeItem: Subtree) => void;
    setSelectedIndicators: (indicators: IndicatorInfo[]) => void;
    toggleIndicator: (indicatorId: string) => void;
    setIsLoadingIndicators: (isLoadingIndicators: boolean) => void;
    /**
     * Cycles through the available marker modes in deterministic order
     */
    cycleMarkerMode: () => void;

    // health range tagging state
    setIsAddingHealthRange: (isAddingHealthRange: boolean) => void;
    setSelectedHealthRangeIds: (selectedHealthRangeIds: string[]) => void;
    setEditingHealthRange: (
        healthRange: IIssueHealthRangeResponse,
        groupId: string
    ) => void;
    stopEditingHealthRange: () => void;
    setMarkerMode: (markerMode: MarkerMode) => void;
}>;

export type WorkbenchStateInitializerMethods = Readonly<{
    /**
     * @param treeItems corresponds to GroupIds in the session
     */
    updateFromSession: (
        session: ISessionResponse,
        treeItems: ITreeItemResponse[]
    ) => void;
    updateFromIndicator: (indicator: IIndicatorResponse) => void;
    updateFromAlert: (alert: IAlertResponse) => void;
    updateFromActivityEvent: (
        activityEvent: IActivityEventResponse,
        groups: ITreeItemResponse[],
        indicators: IIndicatorResponse[]
    ) => void;
    updateFromIssue: (issue: IIssueMachineHealthResponse) => void;
    updateFromChartData: (
        issue: IIssueMachineHealthResponse,
        chartData: UnifiedChartMetadata
    ) => void;
    updateFromTreeItem: (treeItem: ITreeItemResponse) => void;
    updateFromBrowserStorage: (userEmail: string, systemId: string) => void;
}>;
type _PrivateWorkbenchStateMethods = Readonly<{
    setIsInitialized: (value: boolean) => void;
}>;
// if you get a type error here, you probably need to add an entry from WorkbenchStateInitializerMethods
const WorkbenchStateInitializerMethodNames = typeArrayOfAll<
    keyof WorkbenchStateInitializerMethods
>()([
    'updateFromSession',
    'updateFromIndicator',
    'updateFromAlert',
    'updateFromActivityEvent',
    'updateFromIssue',
    'updateFromChartData',
    'updateFromTreeItem',
    'updateFromBrowserStorage',
]);

export function extractWorkbenchStateInitializerMethods(
    state: WorkbenchState
): WorkbenchStateInitializerMethods {
    return Object.fromEntries(
        WorkbenchStateInitializerMethodNames.map((name) => [name, state[name]])
    ) as WorkbenchStateInitializerMethods;
}

export type WorkbenchState = WorkbenchStateValues &
    WorkbenchStateMethods &
    WorkbenchStateInitializerMethods;
/**
 * A type to be used solely within private context in WorkbenchProvider for internal helpers
 */

export type _WorkbenchStateInternal = WorkbenchState & _PrivateWorkbenchStateMethods;

export type StoredWorkbenchStateValues = Omit<WorkbenchStateValues, 'isInitialized'> &
    Readonly<{ systemId?: string }>;
